import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { darken } from 'polished';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { RangeResult } from '../../api/ApiTypes';
import { NotificationData } from '../../api/endpoints/notifications';
import { useAppEvent } from '../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../concepts/resources/Icon';
import { FauxCopy } from '../../concepts/text/FauxCopy';
import { Colors, Spinner } from '../../ui';
import { Position } from '../../ui/Position';
import { FixedScroller, ScrollerValues } from '../../ui/Scrollers';
import { FadeIn } from '../../ui/Transitions';
import { useResolve } from '../Container';
import { EmptyBlock } from '../dashboard/elements/EmptyBlock';
import { Copy, CopyTruncate, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { ProfileModel } from '../ProfileModel';
import { Avatar } from './Avatar';
import { NotificationGroup } from './NotificationGroup';

const Header = styled.div`
    padding: 1.125rem 1.5rem 0.875rem;
    font-size: 1rem;
    font-weight: 450;
    border-radius: 0.375rem 0.375rem 0 0;
    background: ${Colors.gray50};
    border-bottom: solid .0625rem ${Colors.border};
`;

const NotificationFrame = styled.div<{ read?: boolean }>`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    position: relative;
    padding: 1rem 1.25rem;

    .mention {
        background: none !important;
        margin: 0 !important;
        user-select: none;
    }
`;

const External = styled.div`
    position: absolute;
    right: 0;
    top: 50%;
    opacity: 0;
    transform: translate(-.625rem, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const NotificationArtifact = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: stretch;
    gap: .25rem;
    border: solid .0625rem ${Colors.border};
    border-radius: .5rem;
    padding: .5rem .5rem;
    box-shadow: 0 0 .125rem 0 ${Colors.borderLight};
    color: ${Colors.text};
    cursor: pointer;
    user-select: none;

    &:hover {
        border: solid .0625rem ${darken(0.1, Colors.border)};
    }

    &:active {
        background: ${Colors.gray50};
    }

    ${External} {
        opacity: 0;
    }

    &:hover ${External} {
        opacity: 1;
    }
`;

const NotificationQuote = styled.div`
    position: relative;
    background: ${Colors.slate100};
    border-radius: .5rem;
    padding: .5rem .5rem;
    color: ${Colors.text};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    user-select: none;
    cursor: pointer;    

    &:hover {
        background: ${darken(0.05, Colors.slate100)};
    }

    &:active {
        background: ${darken(0.1, Colors.slate100)};
    }

    ${External} {
        opacity: 0;
    }

    &:hover ${External} {
        opacity: 1;
    }
`;

const Hint = styled.div`
    text-align: center;
    font-family: 'Caveat';
    font-size: 1.5rem;
    font-weight: 500;
    color: ${Colors.textMenu};
    transform: rotate(-1.1deg);
`;

interface NotificationListItemProps {
    item: NotificationGroup;
    onSelect: (id: string) => Promise<void>;
}

const NotificationListItem = ({ item, onSelect }: NotificationListItemProps) => {
    if (item.type === 'Mention') return <MentionNotificationListItem item={item} onSelect={onSelect} />;
    if (item.type === 'TaskAssign') return <TaskNotificationListItem item={item} onSelect={onSelect} />;
    return null;
};

const useMentionedBoard = ({ items }: NotificationGroup) => {
    const boards = items.map(x => x.board).filter(x => !!x);
    const ids = new Set(boards.map(x => x.id));
    return ids.size === 1 ? boards[0] : null;
};

const MentionNotificationListItem = ({ item, onSelect }: NotificationListItemProps) => {

    const board = useMentionedBoard(item);

    return <NotificationFrame>
        <Avatar.ForUser user={item.sender} />
        <Flexer flex="column" gap=".5">
            <Copy size=".875" bold flush>
                {item.sender?.name ?? 'Someone'} mentioned you on {board?.label ?? 'multiple boards'}.
            </Copy>
            {item.items.map(x =>
                <a href={`/go/${x.board?.id}/${x.entity?.id}`} key={x.id}>
                    <NotificationQuote>
                        <Flexer padding="0 1.5 0 0" overflow="hidden">
                            <FauxCopy size=".875" value={x.data?.quote?.trim()} />
                        </Flexer>
                        <External>
                            <Icon size="1" src="deqo://icons/hero/external-link" />
                        </External>
                    </NotificationQuote>
                </a>
            )}
            <Copy size=".875" flush>
                {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
            </Copy>
        </Flexer>
    </NotificationFrame>;
};

const TaskNotificationListItem = ({ item, onSelect }: NotificationListItemProps) => {

    return <NotificationFrame>
        <Avatar.ForUser user={item.sender} />
        <Flexer flex="column" gap=".5">
            <Copy size=".875" bold flush>
                {item.sender?.name ?? 'Someone'} assigned {item.items.length === 1 ? 'an action' : `${item.items.length} actions`} to you.
            </Copy>
            {item.items.map(x =>
                <a href={`/go/${x.entity?.id}`} key={x.id}>
                    <NotificationArtifact >
                        <Flexer width="1.5">
                            <Icon src="deqo://icons/dash/actions" size="1.5" color={Colors.primary} />
                        </Flexer>
                        <Flexer inset="0.675 1.5 0 2.25" relative>
                            <CopyTruncate style={{ maxHeight: '1.5rem' }}>
                                <FauxCopy size=".875" value={x.entity?.summary?.trim()} />
                            </CopyTruncate>
                        </Flexer>
                        <External>
                            <Icon size="1" src="deqo://icons/hero/external-link" />
                        </External>
                    </NotificationArtifact>
                </a>
            )}
            <Copy size=".875" flush>
                {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
            </Copy>
        </Flexer>
    </NotificationFrame>;
};

const EmptyFrame = styled.div`
    position: absolute;
    inset: 0;
    padding: 1rem;
    border-radius: 0 0 0.375rem 0.375rem;
    background: #F5F7FE;
`;

const NotificationsEmpty = () => {
    return <EmptyFrame>
        <EmptyBlock
            title="You don&apos;t have any notifications yet!"
            subtitle="When you do, they will show up here.">
            <Hint>
                Pssst... you can mention team mates by typing &apos;@&apos; and<br />
                selecting the person you want to mention
            </Hint>
        </EmptyBlock>
    </EmptyFrame>;
};

const useInfiniteNotificationData = () => {
    const api = useResolve(ApiClient);

    const params = {
        sort: 'createdAt:desc',
        startAt: 0,
        maxResults: 10,
    };

    return useInfiniteQuery({
        queryKey: ['notifications.list'],
        queryFn: async ({ pageParam }) => {
            const result = await api.exec(v2.notifications.list({ ...params, startAt: pageParam || 0 }));
            return result;
        },
        getNextPageParam: (lastPage) => {
            const ll = lastPage.items.length || 0;
            if (!ll || ll < lastPage.maxResults) return undefined;
            return lastPage.startAt + ll;
        },
    });
};

const useNotificationGroups = (data: InfiniteData<RangeResult<NotificationData>>) => {
    return useMemo(() => {
        if (!data) return [];
        return NotificationGroup.from(data.pages.flatMap(x => x.items));
    }, [data]);
};

export const NotificationsView = () => {

    const api = useResolve(ApiClient);
    const me = useResolve(ProfileModel);

    const { data, isInitialLoading, fetchNextPage, hasNextPage } = useInfiniteNotificationData();
    const items = useNotificationGroups(data);
    const qc = useQueryClient();

    useAppEvent('user:notification', (pushData) => {
        if (pushData.userId === me.id) {
            qc.invalidateQueries(['notifications.list']);
            qc.invalidateQueries(['notifications.count']);
        }
    });

    const onScroll = (values: ScrollerValues) => {
        const reachedBottom = values.scrollTop + 1 >= values.scrollHeight - values.clientHeight;
        if (reachedBottom && hasNextPage) fetchNextPage();
    };

    const onSelect = async (id: string) => {
        // await api.exec(v2.notifications.markAsRead([id]));
    };

    useEffect(() => {
        api.exec(v2.notifications.markAsRead('all')).then(() => { });
    }, [open]);

    return <Flexer relative width="38">
        <Header>
            <Flexer flex="row" justify="space-between" align="center">
                <Flexer flex="row" align="center" gap=".5">
                    <Title flush size="lg" color={Colors.text}>Your Notifications</Title>
                </Flexer>
            </Flexer>
        </Header>
        <Flexer relative height="42.5" padding=".5 0">
            {isInitialLoading &&
                <Position at="center">
                    <Spinner size={3} color={Colors.primaryLight} />
                </Position>
            }
            {!isInitialLoading &&
                <FadeIn over="200ms">
                    {!!items.length &&
                        <FixedScroller height="42" onScroll={onScroll}>
                            {items.map(x => <NotificationListItem key={x.id} item={x} onSelect={onSelect} />)}
                        </FixedScroller>
                    }
                    {!items.length && <NotificationsEmpty />}
                </FadeIn>
            }
        </Flexer>
    </Flexer >;
};
